import { Task, Epic, TaskFilters, CreateTaskData, UpdateTaskData, CreateEpicData, TaskComment } from 'types/tasks';
import api, { ApiError } from './apiClient';

class TaskService {
  // GET /api/tasks - Get tasks based on user permissions and filters
  async getTasks(filters?: TaskFilters): Promise<Task[]> {
    try {
      const params: Record<string, any> = {};

      if (filters?.status) params.status = filters.status.join(',');
      if (filters?.priority) params.priority = filters.priority.join(',');
      if (filters?.assignedTo) params.assignedTo = filters.assignedTo.join(',');
      if (filters?.epicId) params.epicId = filters.epicId.join(',');
      if (filters?.teamId) params.teamId = filters.teamId.join(',');
      if (filters?.search) params.search = filters.search;
      if (filters?.dueDate?.from) params.dueDateFrom = filters.dueDate.from.toISOString();
      if (filters?.dueDate?.to) params.dueDateTo = filters.dueDate.to.toISOString();

      const data = await api.get('/tasks', params);

      // Handle the API response format: { success: true, data: [...] }
      const tasks = data.success && Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);

      return tasks.map((task: any) => ({
        id: task._id || task.id,
        title: task.title || 'Sin título',
        description: task.description || '',
        status: task.status || 'todo',
        priority: task.priority || 'medium',
        assignedTo: task.assignedTo?._id || task.assignedTo || '',
        assignedToName: task.assignedTo?.fullName || task.assignedToName || 'Sin asignar',
        assigneeAvatar: task.assignedTo?.avatar || task.assigneeAvatar,
        createdBy: task.createdBy?._id || task.createdBy || '',
        createdByName: task.createdBy?.fullName || task.createdByName || 'Sin autor',
        epicId: task.epic?._id || task.epicId,
        teamId: task.team?._id || task.teamId,
        teamName: task.team?.name || task.teamName,
        dueDate: task.dueDate ? new Date(task.dueDate) : undefined,
        estimatedHours: task.estimatedHours || 0,
        actualHours: task.actualHours || 0,
        tags: task.tags || [],
        attachments: task.attachments || [],
        comments: task.comments || [],
        createdAt: new Date(task.createdAt || Date.now()),
        updatedAt: new Date(task.updatedAt || Date.now())
      }));
    } catch (error: unknown) {
      throw error;
    }
  }

  // GET /api/tasks/epics - Get epics based on user permissions
  async getEpics(teamId?: string): Promise<Epic[]> {
    try {
      const params: Record<string, any> = { limit: 20 };
      if (teamId) params.teamId = teamId;

      const data = await api.get('/tasks/epics', params);

      const epics = data.success && Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);

      return epics.map((epic: any) => ({
        id: epic._id || epic.id,
        title: epic.title || 'Sin título',
        description: epic.description || '',
        status: epic.status || 'planning',
        teamId: epic.team?._id || epic.teamId || '',
        teamName: epic.team?.name || epic.teamName || 'Sin equipo',
        createdBy: epic.createdBy?._id || epic.createdBy || '',
        createdByName: epic.createdBy?.fullName || epic.createdByName || 'Sin autor',
        startDate: epic.startDate ? new Date(epic.startDate) : undefined,
        endDate: epic.endDate ? new Date(epic.endDate) : undefined,
        progress: epic.progress || 0,
        tasks: epic.tasks || [],
        createdAt: new Date(epic.createdAt || Date.now()),
        updatedAt: new Date(epic.updatedAt || Date.now())
      }));
    } catch (error: unknown) {
      throw error;
    }
  }

  // POST /api/tasks - Create new task
  async createTask(taskData: CreateTaskData): Promise<Task> {
    try {
      const data = await api.post('/tasks', taskData);

      const task = data.success ? data.data : (data.task || data);

      return {
        id: task._id || task.id,
        title: task.title || taskData.title,
        description: task.description || taskData.description,
        status: task.status || 'todo',
        priority: task.priority || taskData.priority,
        assignedTo: task.assignedTo?._id || task.assignedTo || taskData.assignedTo,
        assignedToName: task.assignedTo?.fullName || task.assignedToName || 'Sin asignar',
        assigneeAvatar: task.assignedTo?.avatar || task.assigneeAvatar,
        createdBy: task.createdBy?._id || task.createdBy || '',
        createdByName: task.createdBy?.fullName || task.createdByName || 'Sin autor',
        epicId: task.epic?._id || task.epicId || taskData.epicId,
        teamId: task.team?._id || task.teamId || task.team || taskData.team,
        teamName: task.team?.name || task.teamName,
        dueDate: task.dueDate ? new Date(task.dueDate) : taskData.dueDate,
        estimatedHours: task.estimatedHours || taskData.estimatedHours || 0,
        actualHours: 0,
        tags: task.tags || taskData.tags || [],
        attachments: [],
        comments: [],
        createdAt: new Date(task.createdAt || Date.now()),
        updatedAt: new Date(task.updatedAt || Date.now())
      };
    } catch (error: unknown) {
      throw error;
    }
  }

  // POST /api/tasks/epics - Create new epic
  async createEpic(epicData: CreateEpicData): Promise<Epic> {
    try {
      const data = await api.post('/tasks/epics', epicData);

      const epic = data.success ? data.data : (data.epic || data);

      return {
        id: epic._id || epic.id,
        title: epic.title || epicData.title,
        description: epic.description || epicData.description,
        status: epic.status || 'planning',
        teamId: epic.team?._id || epic.teamId || epic.team || epicData.team,
        teamName: epic.team?.name || epic.teamName || 'Sin equipo',
        createdBy: epic.createdBy?._id || epic.createdBy || '',
        createdByName: epic.createdBy?.fullName || epic.createdByName || 'Sin autor',
        startDate: epic.startDate ? new Date(epic.startDate) : undefined,
        endDate: epic.endDate ? new Date(epic.endDate) : undefined,
        progress: 0,
        tasks: [],
        createdAt: new Date(epic.createdAt || Date.now()),
        updatedAt: new Date(epic.updatedAt || Date.now())
      };
    } catch (error: unknown) {
      throw error;
    }
  }

  // PUT /api/tasks/{taskId} - Update task
  async updateTask(updateData: UpdateTaskData): Promise<Task> {
    try {
      const data = await api.put(`/tasks/${updateData.id}`, updateData);

      const task = data.success ? data.data : (data.task || data);

      return {
        id: task._id || task.id,
        title: task.title || 'Sin título',
        description: task.description || '',
        status: task.status || 'todo',
        priority: task.priority || 'medium',
        assignedTo: task.assignedTo?._id || task.assignedTo || '',
        assignedToName: task.assignedTo?.fullName || task.assignedToName || 'Sin asignar',
        assigneeAvatar: task.assignedTo?.avatar || task.assigneeAvatar,
        createdBy: task.createdBy?._id || task.createdBy || '',
        createdByName: task.createdBy?.fullName || task.createdByName || 'Sin autor',
        epicId: task.epic?._id || task.epicId,
        teamId: task.team?._id || task.teamId,
        teamName: task.team?.name || task.teamName,
        dueDate: task.dueDate ? new Date(task.dueDate) : undefined,
        estimatedHours: task.estimatedHours || 0,
        actualHours: task.actualHours || 0,
        tags: task.tags || [],
        attachments: task.attachments || [],
        comments: task.comments || [],
        createdAt: new Date(task.createdAt || Date.now()),
        updatedAt: new Date(task.updatedAt || Date.now())
      };
    } catch (error: unknown) {
      throw error;
    }
  }

  // DELETE /api/tasks/{taskId} - Delete task
  async deleteTask(taskId: string): Promise<void> {
    try {
      await api.delete(`/tasks/${taskId}`);
    } catch (error: unknown) {
      throw error;
    }
  }

  // POST /api/tasks/{taskId}/comments - Add comment to task
  async addTaskComment(taskId: string, content: string): Promise<TaskComment> {
    try {
      const data = await api.post(`/tasks/${taskId}/comments`, { content });

      const comment = data.success ? data.data : (data.comment || data);

      return {
        id: comment._id || comment.id,
        taskId: comment.task || taskId,
        userId: comment.user?._id || comment.userId || '',
        userName: comment.user?.fullName || comment.userName || 'Usuario',
        userAvatar: comment.user?.avatar || comment.userAvatar,
        content: comment.content || content,
        createdAt: new Date(comment.createdAt || Date.now())
      };
    } catch (error: unknown) {
      throw error;
    }
  }

  // PUT /api/tasks/{taskId}/status - Update task status (for Kanban drag & drop)
  async updateTaskStatus(taskId: string, status: Task['status']): Promise<void> {
    try {
      await api.put(`/tasks/${taskId}/status`, { status });
    } catch (error: unknown) {
      throw error;
    }
  }

  // PUT /api/tasks/epics/{epicId} - Update epic
  async updateEpic(epicId: string, epicData: Partial<Epic>): Promise<Epic> {
    try {
      const data = await api.put(`/tasks/epics/${epicId}`, epicData);

      const epic = data.success ? data.data : (data.epic || data);

      return {
        id: epic._id || epic.id,
        title: epic.title || 'Sin título',
        description: epic.description || '',
        status: epic.status || 'planning',
        teamId: epic.team?._id || epic.teamId || '',
        teamName: epic.team?.name || epic.teamName || 'Sin equipo',
        createdBy: epic.createdBy?._id || epic.createdBy || '',
        createdByName: epic.createdBy?.fullName || epic.createdByName || 'Sin autor',
        startDate: epic.startDate ? new Date(epic.startDate) : undefined,
        endDate: epic.endDate ? new Date(epic.endDate) : undefined,
        progress: epic.progress || 0,
        tasks: epic.tasks || [],
        createdAt: new Date(epic.createdAt || Date.now()),
        updatedAt: new Date(epic.updatedAt || Date.now())
      };
    } catch (error: unknown) {
      throw error;
    }
  }

  // DELETE /api/tasks/epics/{epicId} - Delete epic
  async deleteEpic(epicId: string): Promise<void> {
    try {
      await api.delete(`/tasks/epics/${epicId}`);
    } catch (error: unknown) {
      throw error;
    }
  }

  // GET /api/tasks/{taskId} - Get single task by ID
  async getTaskById(taskId: string): Promise<Task | null> {
    try {
      const data = await api.get(`/tasks/${taskId}`);

      const task = data.success ? data.data : (data.task || data);

      return {
        id: task._id || task.id,
        title: task.title || 'Sin título',
        description: task.description || '',
        status: task.status || 'todo',
        priority: task.priority || 'medium',
        assignedTo: task.assignedTo?._id || task.assignedTo || '',
        assignedToName: task.assignedTo?.fullName || task.assignedToName || 'Sin asignar',
        assigneeAvatar: task.assignedTo?.avatar || task.assigneeAvatar,
        createdBy: task.createdBy?._id || task.createdBy || '',
        createdByName: task.createdBy?.fullName || task.createdByName || 'Sin autor',
        epicId: task.epic?._id || task.epicId,
        teamId: task.team?._id || task.teamId,
        teamName: task.team?.name || task.teamName,
        dueDate: task.dueDate ? new Date(task.dueDate) : undefined,
        estimatedHours: task.estimatedHours || 0,
        actualHours: task.actualHours || 0,
        tags: task.tags || [],
        attachments: task.attachments || [],
        comments: task.comments || [],
        createdAt: new Date(task.createdAt || Date.now()),
        updatedAt: new Date(task.updatedAt || Date.now())
      };
    } catch (error: unknown) {
      if ((error as ApiError)?.statusCode === 404) {
        return null;
      }
      throw error;
    }
  }
}

export const taskService = new TaskService();
export default taskService;
